import { useId } from 'react';
import { cn } from '@/utils/style';
import { Checkbox } from './Checkbox';

export interface CheckboxGroupOption<TValue extends string | number> {
  value: TValue;
  label: string;
}

export interface CheckboxGroupProps<TValue extends string | number> {
  value: TValue[];
  onChange: (value: TValue[]) => void;
  options: CheckboxGroupOption<TValue>[];
  selectAllLabel?: string;
  grid?: boolean;
}

const checkboxOptionClassName = 'flex min-w-0 cursor-pointer items-center gap-2 hover:opacity-75';

export function CheckboxGroup<TValue extends string | number>(props: CheckboxGroupProps<TValue>) {
  const { value, onChange, options, selectAllLabel = 'Tous', grid = false } = props;
  const baseId = useId();
  const allValues = options.map((o) => o.value);

  const allSelected = allValues.length > 0 && allValues.every((v) => value.includes(v));
  const toggleOne = (optionValue: TValue) => {
    if (value.includes(optionValue)) {
      onChange(value.filter((v) => v !== optionValue));
    } else {
      onChange([...value, optionValue]);
    }
  };
  const toggleAll = () => {
    if (allSelected) {
      onChange([]);
    } else {
      onChange([...allValues]);
    }
  };
  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={`${baseId}-all`} className={checkboxOptionClassName}>
        <Checkbox id={`${baseId}-all`} checked={allSelected} onCheckedChange={toggleAll} />
        <span className="text-sm">{selectAllLabel}</span>
      </label>
      <div className={cn({ 'flex flex-col gap-1.5 pl-1': !grid, 'grid grid-cols-3 gap-2': grid })}>
        {options.map((option) => (
          <label
            key={String(option.value)}
            htmlFor={`${baseId}-${option.value}`}
            className={checkboxOptionClassName}
          >
            <Checkbox
              id={`${baseId}-${option.value}`}
              checked={value.includes(option.value)}
              onCheckedChange={() => toggleOne(option.value)}
            />
            <span className="text-sm">{option.label}</span>
          </label>
        ))}
      </div>
    </div>
  );
}
